import React, { useState, useEffect, useRef, useContext } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { OnboardingContext } from '../contexts/OnboardingContext';
import OnboardGuard from '../components/OnboardGuard';
import { Shield, Clock, RefreshCw } from 'lucide-react';
import AuthShell from '../components/common/AuthShell';
import { Button } from '../components/ui';

function ConfirmPhoneForm() {
  const navigate = useNavigate();
  const { setOnboardingStepIfNotComplete } = useContext(OnboardingContext);
  const [codeEntered, setCodeEntered] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isResending, setIsResending] = useState(false);

  let phoneId = null;
  try { phoneId = localStorage.getItem('phoneId'); } catch { /* Safari private browsing */ }

  // Reference to the countdown interval
  const intervalRef = useRef(null);

  // On mount: fetch phone record to get expiry, phone number
  useEffect(() => {
    if (!phoneId) {
      navigate('/signup', { replace: true });
      return;
    }

    // Fetch the phone record from backend to get code_expires_at and phone_number
    api
      .get(`/api/sms/phones/${phoneId}`)
      .then((res) => {
        const phone = res.data.phone;
        setPhoneNumber(phone.phone_number);

        // Compute initial remaining seconds
        const expiresAt = new Date(phone.code_expires_at);
        const now = new Date();
        const diff = Math.floor((expiresAt - now) / 1000);
        setRemainingSeconds(diff > 0 ? diff : 0);

        // Start countdown
        intervalRef.current = setInterval(() => {
          setRemainingSeconds((prev) => {
            if (prev <= 1) {
              clearInterval(intervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      })
      .catch((err) => {
        console.error('Failed to fetch phone record:', err);
        setError('Failed to retrieve code expiry. Please try again.');
      });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [phoneId, navigate]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    if (!codeEntered.trim()) {
      setError('Please enter the confirmation code.');
      return;
    }
    if (!phoneId) {
      setError('Missing authentication.');
      return;
    }
    setLoading(true);

    try {
      const res = await api.post('/api/sms/confirm-code', {
        phoneId,
        codeEntered,
      });
      if (res.data.success) {
        // Phone confirmed — navigate to dashboard (phone setup is post-onboarding now)
        navigate('/dashboard');
      } else {
        setError(res.data.error || 'Invalid or expired code.');
      }
    } catch (err) {
      console.error('Confirmation error:', err);
      const msg = err.response?.data?.error || 'Confirmation failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    if (!phoneId) {
      setError('Missing authentication.');
      return;
    }
    setIsResending(true);

    try {
      const res = await api.post('/api/sms/resend-code', {
        phoneId,
      });
      if (res.data.success) {
        // Reset countdown to 60 seconds
        setRemainingSeconds(60);
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
          setRemainingSeconds((prev) => {
            if (prev <= 1) {
              clearInterval(intervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError('Failed to resend code. Please try again.');
      }
    } catch (err) {
      console.error('Resend code error:', err);
      const msg = err.response?.data?.error || 'Failed to resend code.';
      setError(msg);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthShell>
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-purple-100 p-3 rounded-full">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center">
                Verify Your Phone
              </h1>
              <p className="text-gray-600 text-center">
                We sent a 6-digit code to
              </p>
              <p className="text-gray-900 font-semibold text-center mt-1">
                {phoneNumber}
              </p>
            </div>

            {error && (
              <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <div className="font-semibold">⚠️ Error</div>
                <p className="mt-1">{error}</p>
              </div>
            )}

            <form onSubmit={handleConfirm} className="space-y-5">
              {/* Confirmation Code Input */}
              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2 text-center">
                  Enter Confirmation Code
                </label>
                <input
                  type="text"
                  placeholder="000000"
                  value={codeEntered}
                  onChange={(e) => setCodeEntered(e.target.value)}
                  className="input-field w-full text-center text-2xl tracking-widest font-mono"
                  required
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </div>

              {/* Timer / Resend */}
              <div className="text-center">
                {remainingSeconds > 0 ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>
                      Code expires in <strong>{remainingSeconds}</strong> second{remainingSeconds !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                    disabled={isResending}
                  >
                    <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                    {isResending ? 'Resending…' : 'Resend Code'}
                  </button>
                )}
              </div>

              {/* Submit button */}
              <Button type="submit" variant="primary" fullWidth disabled={loading} loading={loading}>
                {loading ? 'Verifying' : 'Verify & Continue'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-base sm:text-sm text-gray-500">
                Didn't receive the code? Check your messages or try resending after the timer expires.
              </p>
            </div>
    </AuthShell>
  );
}

// Wrap the confirmation form in OnboardGuard
export default function ConfirmPhone() {
  return (
    <OnboardGuard currentStep="confirm_phone">
      <ConfirmPhoneForm />
    </OnboardGuard>
  );
}